import {BitmapText} from "pixi.js";
import {DrawFrmThing} from "../drawfrm/DrawFrmThing";
import {ElevationApp} from "../ElevationApp";
import {FrmRefPoint} from "../drawfrm/FrmRefPoint";
import {FrmVar} from "../drawfrm/FrmVar";
import { getSwingSide } from "../scenes/frameProfile/Helpers";
import {lineBlueGrey, lineBlueHeavy, lineBlueLight, lineGreenGrey} from "../const";

// how far open the swing-direction arc is drawn, in degrees
const defaultSwingAngleDeg = 15;

// units are mm (see ElevationApp/const.ts); the scene auto-scales to fit.
export class DoorTopDown extends DrawFrmThing {

    private elev: ElevationApp;
    private active: boolean;
    private swingAngleDeg: number;

    // door dimensions, dynamic: width is the (calculated) active/inactive leaf
    // width, height is the door thickness. The corners and the drawn rectangle
    // track these and refresh whenever they change.
    private width: FrmVar;
    private height: FrmVar;

    topLeft!: FrmRefPoint;
    bottomLeft!: FrmRefPoint;
    topRight!: FrmRefPoint;
    bottomRight!: FrmRefPoint;


    // "active"/"inactive" leaf label, only shown when the door is part of a pair.
    // created lazily in draw() and centered on the door.
    private label: BitmapText | null = null;

    constructor(elev: ElevationApp, active: boolean, swingAngleDeg: number = defaultSwingAngleDeg) {
        super();

        this.elev = elev;
        this.active = active;
        this.swingAngleDeg = swingAngleDeg;

        this.width = active ? elev.frameSpec.activeDoorWidth : elev.frameSpec.inActiveDoorWidth;
        this.height = elev.frameSpec.doorFrameProfile.doorThickness;

        // console.log("door", active, this.width.get() / inches)

        const w = this.width.get();
        const h = this.height.get();

        FrmRefPoint.buildGroup(() => {
            this.topLeft = this.makeRef("door:topLeft", 0, 0);
            this.bottomLeft = this.makeRef("door:bottomLeft", 0, h);
            this.topRight = this.makeRef("door:topRight", w, 0);
            this.bottomRight = this.makeRef("door:bottomRight", w, h);
        });

        const onChange = () => this.update();
        this.trackSubscription(this.width.subscribe(onChange));
        this.trackSubscription(this.height.subscribe(onChange));

        this.draw();
    }

    private update() {
        const w = this.width.get();
        const h = this.height.get();

        this.topLeft.calculatedPoint?.set(0, 0);
        this.bottomLeft.calculatedPoint?.set(0, h);
        this.topRight.calculatedPoint?.set(w, 0);
        this.bottomRight.calculatedPoint?.set(w, h);
    }

    draw() {
        const width = this.width.get();
        const height = this.height.get();

        this.update();

        const g = this.lines;
        g.clear();
        g.lineStyle(1, lineBlueHeavy, 1);
        if(this.active) {
            g.beginFill(lineGreenGrey, 1);
        } else {
            g.beginFill(lineBlueGrey, 1);
        }

        g.drawRect(0, 0, width, height);
        g.endFill();

        // arc traced by the door's swinging (free) edge as it opens, to show
        // which way it swings. A double-acting door swings both ways, so draw a
        // sweep on each side.
        const swingInfo = getSwingSide(this.elev, this.active);
        if (swingInfo.direction === "both") {
            this.drawSwing(width, height, swingInfo.side, "in");
            this.drawSwing(width, height, swingInfo.side, "out");
        } else {
            this.drawSwing(width, height, swingInfo.side, swingInfo.direction);
        }

        this.drawLabel(width, height);

    }

    // draws a tapered ribbon tracing the path of the door's swinging (free) edge
    // as it opens `swingAngleDeg` in the given direction. Centered on the hinge
    // corner; `direction` picks which side (out: +y, in: -y) the free end swings to.
    private drawSwing(width: number, height: number, side: "left" | "right", direction: "in" | "out") {
        const g = this.lines;

        const startWidth = 4;
        const hingeX = side === "left" ? (0 - startWidth) : (width + startWidth);
        const hingeY = direction === "out" ? height : 0; // pivot on bottom when swinging out
        const hingeSign = side === "left" ? 1 : -1; // mirror for a right hinge
        const swingSign = direction === "out" ? 1 : -1;

        const theta = (this.swingAngleDeg * Math.PI) / 180;

        // the swinging edge sits a full door width from the hinge along the
        // closed door. atan2 gives that closed-edge angle (0 for a left hinge,
        // π for a right hinge); the door sweeps `theta` from there.
        const radius = width;
        const startAngle = Math.atan2(0, width - 2 * hingeX);
        const sweep = theta * hingeSign * swingSign;

        // draw the swing as a tapered ribbon centered on the swept path: thick
        // where it leaves the door's free edge, tapering to a point at the tip.
        // since the path is a circle about the hinge, "perpendicular" is radial,
        // so the taper is just a shrinking offset on the radius.
        const segs = 32;
        const startHalf = startWidth; // full width ≈ door thickness at the base
        const endHalf = 0;              // pointed tip

        const outer: number[] = [];
        const inner: number[] = [];
        for (let i = 0; i <= segs; i++) {
            const t = i / segs;
            const ang = startAngle + sweep * t;
            const half = startHalf + (endHalf - startHalf) * t;
            const cos = Math.cos(ang);
            const sin = Math.sin(ang);
            outer.push(hingeX + (radius + half) * cos, hingeY + (radius + half) * sin);
            inner.push(hingeX + (radius - half) * cos, hingeY + (radius - half) * sin);
        }

        // outer edge forward, inner edge back, to close the ribbon
        const ribbon = outer.slice();
        for (let i = inner.length - 2; i >= 0; i -= 2) {
            ribbon.push(inner[i], inner[i + 1]);
        }

        g.lineStyle(0);
        g.beginFill(lineBlueLight, 0.5);
        g.drawPolygon(ribbon);
        g.endFill();
    }

    // label the leaf "active"/"inactive" when this door is part of a pair.
    // single openings don't need the distinction, so no label is drawn.
    private drawLabel(width: number, height: number) {
        if (!this.elev.frameSpec.isPair) {
            if (this.label) this.label.visible = false;
            return;
        }

        if (!this.label) {
            this.label = new BitmapText("", {fontName: "Arial", fontSize: 10});
            this.label.anchor.set(0.5, 0.5);
            this.label.tint = 0x000000;
            this.lines.addChild(this.label);
        }

        this.label.visible = true;
        this.label.text = this.active ? "ACTIVE" : "INACTIVE";
        this.label.position.set(width / 2, height / 2);
    }
}
